import type { Annotation } from 'api';

const DESKTOP_WIDTH_PADDING = 16;
const getDesktopMaxWidth = () => {
  if (typeof window !== 'undefined') {
    return window.innerWidth * 0.66667 - DESKTOP_WIDTH_PADDING;
  }
  return 2560;
};

const getMobileMaxWidth = () => {
  if (typeof window !== 'undefined') {
    return window.innerWidth;
  }
  return 400;
};

const getTargetMaxHeight = () => {
  if (typeof window !== 'undefined') {
    return window.innerHeight * 0.8;
  }
  return 650;
};
export interface MobileZoom {
  isZooming: boolean;
  scale: number;
  translateX: number;
  translateY: number;
}

export interface CanvasConfig {
  devicePixelRatio: number;
  scaledWidth: number;
  scaledHeight: number;
  scale: number;
}

export interface CanvasDimensions {
  width: number;
  height: number;
  scale: number;
}

export interface RenderCanvasOptions {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  annotations: Annotation[];
  hiddenCategories: readonly string[];
  excludedFromDarkeningLabels?: ReadonlySet<string>;
  labelColourMap: Map<string, readonly number[]>;
  isMobile: boolean;
  shouldDarken: boolean;
  zoom?: MobileZoom;
  image: HTMLImageElement;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
  onError?: () => void;
  onScaleCalculated?: (scale: number) => void;
}

export const calculateCanvasDimensions = (
  img: HTMLImageElement,
  maxWidth: number,
  maxHeight: number
): CanvasDimensions => {
  const widthScale = maxWidth / img.width;
  const heightScale = maxHeight / img.height;
  const scale = Math.min(widthScale, heightScale);

  return {
    height: img.height * scale,
    scale,
    width: img.width * scale,
  };
};

export const configureCanvas = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  dimensions: CanvasDimensions
): CanvasConfig => {
  const devicePixelRatio = window.devicePixelRatio || 1;
  const { width: scaledWidth, height: scaledHeight } = dimensions;

  // Set canvas internal resolution for high-DPI displays
  canvas.width = scaledWidth * devicePixelRatio;
  canvas.height = scaledHeight * devicePixelRatio;

  // Scale canvas back down using CSS
  canvas.style.width = `${scaledWidth}px`;
  canvas.style.height = `${scaledHeight}px`;

  // Scale the drawing context
  ctx.scale(devicePixelRatio, devicePixelRatio);

  // Configure for high quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  return {
    devicePixelRatio,
    scale: dimensions.scale,
    scaledHeight,
    scaledWidth,
  };
};

const drawSegment = (
  ctx: CanvasRenderingContext2D,
  segment: number[],
  colour: string,
  scale: number,
  lineWidth: number
): void => {
  ctx.strokeStyle = colour;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();

  for (let i = 0; i < segment.length; i += 2) {
    const x = segment[i] * scale;
    const y = segment[i + 1] * scale;
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }

  ctx.closePath();
  ctx.stroke();
};

const createMaskCanvas = (
  width: number,
  height: number,
  devicePixelRatio: number,
  fillStyle: string
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null => {
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = width;
  maskCanvas.height = height;

  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) return null;

  // Configure mask canvas to match main canvas scaling
  maskCtx.scale(devicePixelRatio, devicePixelRatio);
  maskCtx.imageSmoothingEnabled = true;
  maskCtx.imageSmoothingQuality = 'high';
  maskCtx.fillStyle = fillStyle;
  maskCtx.fillRect(0, 0, width / devicePixelRatio, height / devicePixelRatio);

  return { canvas: maskCanvas, ctx: maskCtx };
};

export const drawSegmentationMask = (
  maskCtx: CanvasRenderingContext2D,
  annotations: Annotation[],
  scale: number,
  hiddenCategories: readonly string[],
  excludedFromDarkeningLabels: ReadonlySet<string> | undefined,
  partialRemovalAlpha: number
): void => {
  maskCtx.globalCompositeOperation = 'destination-out';

  for (const { segmentation, label } of annotations) {
    if (hiddenCategories.includes(label)) continue;
    const isExcluded = excludedFromDarkeningLabels?.has(label);
    const alpha = isExcluded ? partialRemovalAlpha : 1;
    if (alpha <= 0) continue;

    maskCtx.globalAlpha = alpha;
    for (const segment of segmentation) {
      maskCtx.beginPath();

      for (let i = 0; i < segment.length; i += 2) {
        const x = segment[i] * scale;
        const y = segment[i + 1] * scale;
        i === 0 ? maskCtx.moveTo(x, y) : maskCtx.lineTo(x, y);
      }

      maskCtx.closePath();
      maskCtx.fill();
    }
  }

  maskCtx.globalAlpha = 1;
};

export const getTouchDistance = (touches: TouchList): number => {
  if (touches.length < 2) return 0;
  const touch1 = touches[0];
  const touch2 = touches[1];
  return Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) + Math.pow(touch2.clientY - touch1.clientY, 2));
};

export const getTouchCenter = (touches: TouchList): { x: number; y: number } => {
  if (touches.length < 2) return { x: 0, y: 0 };
  const touch1 = touches[0];
  const touch2 = touches[1];
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  };
};

const applyZoomTransform = (
  ctx: CanvasRenderingContext2D,
  zoom: MobileZoom,
  canvasWidth: number,
  canvasHeight: number
): void => {
  if (zoom.scale > 1) {
    ctx.save();
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    ctx.translate(centerX + zoom.translateX, centerY + zoom.translateY);
    ctx.scale(zoom.scale, zoom.scale);
    ctx.translate(-centerX, -centerY);
  }
};

const restoreZoomTransform = (ctx: CanvasRenderingContext2D, zoom: MobileZoom): void => {
  if (zoom.scale > 1) {
    ctx.restore();
  }
};

export const transformPoint = (
  x: number,
  y: number,
  canvasWidth: number,
  canvasHeight: number,
  zoom: MobileZoom,
  margin:
    | number
    | {
        left?: number;
        right?: number;
        top?: number;
        bottom?: number;
      } = 0
): { x: number; y: number; isVisible: boolean } => {
  if (!zoom || zoom.scale <= 1) {
    return { isVisible: true, x, y };
  }

  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;

  const transformedX = (x - centerX) * zoom.scale + centerX + zoom.translateX;
  const transformedY = (y - centerY) * zoom.scale + centerY + zoom.translateY;

  const margins = typeof margin === 'number' ? { bottom: margin, left: margin, right: margin, top: margin } : margin;

  const leftMargin = margins.left ?? 0;
  const rightMargin = margins.right ?? 0;
  const topMargin = margins.top ?? 0;
  const bottomMargin = margins.bottom ?? 0;

  const isVisible =
    transformedX >= -leftMargin &&
    transformedX <= canvasWidth + rightMargin &&
    transformedY >= -topMargin &&
    transformedY <= canvasHeight + bottomMargin;

  return {
    isVisible,
    x: transformedX,
    y: transformedY,
  };
};

export const reverseTransformPoint = (
  x: number,
  y: number,
  canvasWidth: number,
  canvasHeight: number,
  zoom: MobileZoom
): { x: number; y: number } => {
  if (!zoom || zoom.scale <= 1) {
    return { x, y };
  }

  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;

  const originalX = (x - centerX - zoom.translateX) / zoom.scale + centerX;
  const originalY = (y - centerY - zoom.translateY) / zoom.scale + centerY;

  return {
    x: originalX,
    y: originalY,
  };
};

const drawAnnotationsWithDarkening = (
  ctx: CanvasRenderingContext2D,
  annotations: Annotation[],
  config: CanvasConfig,
  hiddenCategories: readonly string[],
  excludedFromDarkeningLabels: ReadonlySet<string> | undefined,
  labelColourMap: Map<string, readonly number[]>,
  formatRgb: (rgb: number[]) => string,
  isMobile: boolean,
  outsideDarknessLevel = 0.8,
  insideDarknessLevel = 0.45
): void => {
  const { devicePixelRatio, scaledWidth, scaledHeight, scale } = config;

  const clampedInsideDarknessLevel = Math.min(Math.max(insideDarknessLevel, 0), outsideDarknessLevel);
  const partialRemovalAlpha = outsideDarknessLevel > 0 ? 1 - clampedInsideDarknessLevel / outsideDarknessLevel : 1;

  const maskResult = createMaskCanvas(
    scaledWidth * devicePixelRatio,
    scaledHeight * devicePixelRatio,
    devicePixelRatio,
    `rgba(0, 0, 0, ${outsideDarknessLevel})`
  );

  if (!maskResult) return;

  const { canvas: maskCanvas, ctx: maskCtx } = maskResult;

  drawSegmentationMask(maskCtx, annotations, scale, hiddenCategories, excludedFromDarkeningLabels, partialRemovalAlpha);

  for (const { segmentation, label, rgb_colour } of annotations) {
    if (hiddenCategories.includes(label)) continue;

    const colourToUse = labelColourMap.get(label) || rgb_colour;
    const strokeColour = formatRgb([...colourToUse]);
    const lineWidth = isMobile ? 2 : 3;

    for (const segment of segmentation) {
      drawSegment(ctx, segment, strokeColour, scale, lineWidth);
    }
  }

  ctx.drawImage(maskCanvas, 0, 0, scaledWidth, scaledHeight);

  if (excludedFromDarkeningLabels && excludedFromDarkeningLabels.size > 0) {
    for (const { segmentation, label, rgb_colour } of annotations) {
      if (hiddenCategories.includes(label)) continue;
      if (!excludedFromDarkeningLabels.has(label)) continue;

      const colourToUse = labelColourMap.get(label) || rgb_colour;
      const strokeColour = formatRgb([...colourToUse]);
      const lineWidth = isMobile ? 2 : 3;

      for (const segment of segmentation) {
        drawSegment(ctx, segment, strokeColour, scale, lineWidth);
      }
    }
  }
};

const drawAnnotationsOnly = (
  ctx: CanvasRenderingContext2D,
  annotations: Annotation[],
  config: CanvasConfig,
  hiddenCategories: readonly string[],
  labelColourMap: Map<string, readonly number[]>,
  formatRgb: (rgb: number[]) => string,
  isMobile: boolean
): void => {
  const { scale } = config;

  for (const { segmentation, label, rgb_colour } of annotations) {
    if (hiddenCategories.includes(label)) continue;

    const colourToUse = labelColourMap.get(label) || rgb_colour;
    const strokeColour = formatRgb([...colourToUse]);
    const lineWidth = isMobile ? 2 : 3;

    for (const segment of segmentation) {
      drawSegment(ctx, segment, strokeColour, scale, lineWidth);
    }
  }
};

export const applyEnhancementFilter = (ctx: CanvasRenderingContext2D) => {
  ctx.filter = 'contrast(1) brightness(1) saturate(1)';
};

export const resetFilter = (ctx: CanvasRenderingContext2D) => {
  ctx.filter = 'none';
};

const formatRgb = (rgb: number[]): string => `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;

export const drawAnnotationsWithTransform = (
  context: CanvasRenderingContext2D,
  annotations: Annotation[],
  hiddenCategories: readonly string[],
  labelColourMap: Map<string, readonly number[]>,
  sourceX: number,
  sourceY: number,
  sourceWidth: number,
  sourceHeight: number,
  canvasSize = 250
) => {
  context.save();

  const scaleX = canvasSize / sourceWidth;
  const scaleY = canvasSize / sourceHeight;

  context.scale(scaleX, scaleY);
  context.translate(-sourceX, -sourceY);

  for (const { segmentation, label, rgb_colour } of annotations) {
    if (hiddenCategories.includes(label)) continue;

    const colourToUse = labelColourMap.get(label) || rgb_colour;
    const strokeColour = formatRgb([...colourToUse]);
    const lineWidth = 4 / Math.min(scaleX, scaleY);

    for (const segment of segmentation) {
      context.strokeStyle = strokeColour;
      context.lineWidth = lineWidth;
      context.beginPath();

      for (let i = 0; i < segment.length; i += 2) {
        const x = segment[i];
        const y = segment[i + 1];

        if (i === 0) {
          context.moveTo(x, y);
        } else {
          context.lineTo(x, y);
        }
      }

      context.closePath();
      context.stroke();
    }
  }

  context.restore();
};

export const drawImageWithFallback = (
  ctx: CanvasRenderingContext2D,
  sourceCanvas: HTMLCanvasElement,
  sourceRect: DOMRect,
  mousePosition: { x: number; y: number },
  zoomLevel: number,
  canvasSize = 250
) => {
  const canvasX = mousePosition.x - sourceRect.left;
  const canvasY = mousePosition.y - sourceRect.top;
  const zoomRadius = 50 / zoomLevel;

  const sourceCanvasDevicePixelRatio = sourceCanvas.width / sourceRect.width;
  let sourceX = (canvasX - zoomRadius) * sourceCanvasDevicePixelRatio;
  let sourceY = (canvasY - zoomRadius) * sourceCanvasDevicePixelRatio;
  const sourceWidth = zoomRadius * 2 * sourceCanvasDevicePixelRatio;
  const sourceHeight = zoomRadius * 2 * sourceCanvasDevicePixelRatio;

  sourceX = Math.max(0, Math.min(sourceX, sourceCanvas.width - sourceWidth));
  sourceY = Math.max(0, Math.min(sourceY, sourceCanvas.height - sourceHeight));

  ctx.clearRect(0, 0, canvasSize, canvasSize);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  try {
    applyEnhancementFilter(ctx);
    ctx.drawImage(sourceCanvas, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvasSize, canvasSize);
    resetFilter(ctx);
  } catch {
    resetFilter(ctx);
  }
};

export const renderCanvas = (options: RenderCanvasOptions) => {
  const {
    canvas,
    ctx,
    annotations,
    hiddenCategories,
    excludedFromDarkeningLabels,
    labelColourMap,
    isMobile,
    shouldDarken,
    zoom,
    image,
    onLoadStart,
    onLoadEnd,
    onError,
    onScaleCalculated,
  } = options;

  const targetMaxWidth = isMobile ? getMobileMaxWidth() : getDesktopMaxWidth();

  const renderImage = () => {
    onLoadEnd?.();

    const dimensions = calculateCanvasDimensions(image, targetMaxWidth, getTargetMaxHeight());
    const config = configureCanvas(canvas, ctx, dimensions);

    onScaleCalculated?.(config.scale);

    ctx.clearRect(0, 0, config.scaledWidth, config.scaledHeight);

    if (zoom && isMobile) {
      applyZoomTransform(ctx, zoom, config.scaledWidth, config.scaledHeight);
    }

    ctx.drawImage(image, 0, 0, config.scaledWidth, config.scaledHeight);

    if (annotations.length > 0) {
      if (shouldDarken) {
        drawAnnotationsWithDarkening(
          ctx,
          annotations,
          config,
          hiddenCategories,
          excludedFromDarkeningLabels,
          labelColourMap,
          formatRgb,
          isMobile
        );
      } else {
        drawAnnotationsOnly(ctx, annotations, config, hiddenCategories, labelColourMap, formatRgb, isMobile);
      }
    }

    if (zoom && isMobile) {
      restoreZoomTransform(ctx, zoom);
    }
  };

  if (image.complete && image.naturalWidth > 0) {
    renderImage();
    return;
  }

  onLoadStart?.();

  const handleLoad = () => {
    image.removeEventListener('load', handleLoad);
    image.removeEventListener('error', handleError);
    renderImage();
  };

  const handleError = () => {
    image.removeEventListener('load', handleLoad);
    image.removeEventListener('error', handleError);
    onLoadEnd?.();
    onError?.();
  };

  image.addEventListener('load', handleLoad);
  image.addEventListener('error', handleError);

  return () => {
    image.removeEventListener('load', handleLoad);
    image.removeEventListener('error', handleError);
  };
};
